import React from 'react';

import { Flex } from '@chakra-ui/react';
import { getApplicableL2MyDataModules } from 'Deer/components/navigation/secondary/ModuleSection';
import { getApplicableL2MyTeamModules } from 'Deer/components/navigation/secondary/MyTeam/MyTeamModules';
import { L3_PATHS } from 'Deer/constants/routes';
import { useDeerTeamType } from 'Deer/hooks/useDeerTeamType';
import { DeerBreadcrumbs } from 'Deer/intent/components/Layout/Layout';
import { useSelectIsL2Visible } from 'Deer/slice/DeerNav.slice';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useHistory, useLocation } from 'react-router-dom';
import useOneOnOnePageParams from 'screens/oneOnOneRabbit/hooks/useOneOnOnePageParams';
import { checkIntentLandingPagesFeatureEnabled, checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { ArrowBack } from 'assets/assetsV1/iconComponents';

import { isMobileView } from 'constants/isMobileView';
import { isNetchexLogin } from 'constants/netChexConstants';
import { routes } from 'constants/routes';

import { BreadCrumb, BreadcrumbItem, BreadcrumbLink } from 'uiToolkitV1/BreadCrumb';
import { Button } from 'uiToolkitV1/Button';
import { Paper } from 'uiToolkitV1/Paper';
import { TextHeader2, TextLabel } from 'uiToolkitV1/Typography';

import { useOneOnOneInstanceDetails } from '../hooks/dataHooks';

const NewDeerBreadcrumbs = ({ employeeDetails }) => {
  const teamType = useDeerTeamType();
  const { moduleName } = useOrganisationSettings();

  const applicableL2MyDataModules = getApplicableL2MyDataModules(moduleName) ?? [];
  const applicableL2DirectReportsModules = getApplicableL2MyTeamModules('directReports', moduleName);
  const applicableL2AllReportsModules = getApplicableL2MyTeamModules('allReports', moduleName);

  let firstLabel;
  let firstLabelTo;
  let secondLabelTo;

  if (teamType === 'allReports') {
    firstLabel = 'Skip Team';
    firstLabelTo = applicableL2AllReportsModules[0]?.value;
    secondLabelTo = L3_PATHS.team.allReports.oneOnOne.manage;
  } else if (teamType === 'directReports') {
    firstLabel = 'Direct Team';
    firstLabelTo = applicableL2DirectReportsModules[0]?.value;
    secondLabelTo = L3_PATHS.team.directReports.oneOnOne.manage;
  } else {
    firstLabel = 'Me';
    firstLabelTo = applicableL2MyDataModules[0]?.value;
    secondLabelTo = L3_PATHS['my-data'].oneOnOne.myOneOnOne;
  }

  return (
    <DeerBreadcrumbs
      breadcrumbs={{
        items: [
          {
            label: firstLabel,

            to: firstLabelTo,
          },
          {
            label: '1:1',
            to: secondLabelTo,
          },
        ],
        page: {
          label: `${employeeDetails?.displayName}`,
          to: '',
        },
      }}
    />
  );
};

const Header = () => {
  const { masterId, instanceId } = useOneOnOnePageParams();
  const teamType = useDeerTeamType();

  const isL2NavVisible = useSelectIsL2Visible();

  let path;
  if (teamType === 'directReports') {
    path = L3_PATHS.team.directReports.oneOnOne.manage;
  } else if (teamType === 'allReports') {
    path = L3_PATHS.team.allReports.oneOnOne.manage;
  } else {
    path = L3_PATHS['my-data'].oneOnOne.myOneOnOne;
  }
  const route = checkNewNavEnabled() ? path : routes.oneOnOne.index;

  let breadCrumbsText;
  if (teamType === 'directReports') {
    breadCrumbsText = "My direct team's 1:1s";
  } else if (teamType === 'allReports') {
    breadCrumbsText = "My skip team's 1:1s";
  } else {
    breadCrumbsText = 'My 1:1s';
  }

  const { employeeDetails, isArchived } = useOneOnOneInstanceDetails({
    masterId,
    instanceId,
  });
  const history = useHistory();
  const { search } = useLocation();
  const goBack = () => {
    if (isArchived) {
      const queryParams = new URLSearchParams(search);
      queryParams.set('oneOnOneType', 'ARCHIVED');
      history.push({
        pathname: route,
        search: queryParams.toString(),
      });
    } else {
      history.push(route);
    }
  };

  return (
    <Paper
      style={{
        background: !checkNewNavEnabled ? '#F5F7F9' : 'transparent',
        borderRadius: 0,
        boxShadow: !checkNewNavEnabled ? '0px 1px 4px #CFD7DF' : 'none',
        padding: isMobileView() ? '8px 12px' : '8px 0',
        marginLeft: !isL2NavVisible && !isMobileView() && '24px',
      }}
    >
      <Flex justifyContent="flex-start" alignItems="center">
        <Button mr="8px" StartIcon={ArrowBack} variant="tertiary" onClick={goBack} data-testid="header-go-back" />
        <Flex flexDir="column">
          {checkIntentLandingPagesFeatureEnabled() && !isNetchexLogin && (
            <NewDeerBreadcrumbs employeeDetails={employeeDetails} />
          )}
          {!checkIntentLandingPagesFeatureEnabled() && (
            <BreadCrumb>
              <BreadcrumbItem>
                <BreadcrumbLink to={route}>{breadCrumbsText}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <TextLabel bold>{employeeDetails?.displayName}</TextLabel>
              </BreadcrumbItem>
            </BreadCrumb>
          )}

          <TextHeader2 bold>1:1 with {employeeDetails?.displayName}</TextHeader2>
        </Flex>
      </Flex>
    </Paper>
  );
};

export { Header };
